/* eslint-disable no-bitwise */
import { createImageData } from "canvas";
import { rgbaNumberToHexString } from "../utils/colour-conversions.ts";
import {
	renderBrickToPixelChannelRows,
	renderBrickUnitToPixelChannelRows,
} from "./render-bricks.ts";
import type { GenericPosition } from "../model/index.ts";
import type { CanvasWithSourceDimensions } from "./canvas-types.ts";
import type { BuildInput } from "./build-input.ts";
import { normaliseBuildInput } from "./build-input.ts";
import { pixAPicKit } from "../pix-a-pic.ts";
import type { BrickSpriteCache } from "./brick-sprite-cache.ts";

// Inspects a freshly rasterised sprite for an empty getImageData readback - the
// device-specific failure (e.g. Samsung Chrome, the Android emulator) where the
// canvas->CPU readback intermittently returns a fully black tile. A `degenerate`
// sprite is one that came back all-black for a non-black colour. colourRgba is
// packed 0xRRGGBBAA.
function inspectSpriteReadback(
	pixelRows: readonly Uint8ClampedArray[],
	colourRgba: number,
): {
	readonly degenerate: boolean;
	readonly nonBlackPixelCount: number;
	readonly totalPixelCount: number;
} {
	const r = (colourRgba >>> 24) & 0xff;
	const g = (colourRgba >>> 16) & 0xff;
	const b = (colourRgba >>> 8) & 0xff;
	// Near-black colours legitimately rasterise to (near) all-black pixels, so a
	// black readback there is indistinguishable from a good one - don't flag them.
	const expectedNearBlack = r <= 16 && g <= 16 && b <= 16;
	let nonBlackPixelCount = 0;
	let totalPixelCount = 0;
	for (const row of pixelRows) {
		for (let i = 0; i < row.length; i += 4) {
			totalPixelCount++;
			if (row[i] !== 0 || row[i + 1] !== 0 || row[i + 2] !== 0) {
				nonBlackPixelCount++;
			}
		}
	}
	return {
		nonBlackPixelCount,
		totalPixelCount,
		// A real sprite - even a dark one - has gradient/notch variation, so an
		// all-black readback for a non-black colour means getImageData returned an
		// empty tile: the flaky readback we're hunting.
		degenerate: !expectedNearBlack && nonBlackPixelCount === 0,
	};
}

function* renderPictureAsBricks(
	ctx: CanvasImageData,
	buildInput: BuildInput,
	brickSizePixels: number,
	ceilBrickSizePixels: number,
	offset: GenericPosition<number>,
	utilityCtx: Parameters<typeof renderBrickToPixelChannelRows>[0] &
		CanvasWithSourceDimensions,
	// Optional cross-render sprite cache from a previous render. Sprites are reused
	// when it was built at this same brick size, otherwise it's ignored and the
	// cache rebuilt from scratch. The freshly populated cache is the generator's
	// return value - thread it back in to reuse sprites on the next render.
	previousSpriteCache?: BrickSpriteCache,
): Generator<undefined, BrickSpriteCache> {
	if (
		utilityCtx.canvas.width < ceilBrickSizePixels ||
		utilityCtx.canvas.height < ceilBrickSizePixels
	) {
		throw new Error(
			`Cache canvas (${utilityCtx.canvas.width}x${utilityCtx.canvas.height}) too small for brick size ${ceilBrickSizePixels}`,
		);
	}

	const {
		noBrickColour,
		data,
		width: bitmapWidth,
		height: bitmapHeight,
	} = normaliseBuildInput(buildInput);

	// Sprite cache, keyed by numeric colour key. Seed from the previous cache when
	// it was built at this brick size (copying so the passed-in cache stays
	// immutable), else start empty. Populated during the render and returned.
	const brickColourCache: Map<number, readonly Uint8ClampedArray[]> =
		previousSpriteCache !== undefined &&
		previousSpriteCache.ceilBrickSizePixels === ceilBrickSizePixels
			? new Map(previousSpriteCache.cache)
			: new Map();

	// Pre-compute dimensions and values used in the loop
	const oversizedImageDataWidth = Math.ceil(
		(bitmapWidth - 1) * brickSizePixels + ceilBrickSizePixels,
	);
	const oversizedImageDataHeight = Math.ceil(
		(bitmapHeight - 1) * brickSizePixels + ceilBrickSizePixels,
	);
	const newData = new Uint8ClampedArray(
		oversizedImageDataWidth * oversizedImageDataHeight * 4,
	);

	// Cache the no brick color for faster comparisons
	const noBrickColourRgba = noBrickColour?.rgba;
	const basePlateColourRgba = pixAPicKit.basePlateColour.rgba;

	// Go backwards because the shadow is to the bottom right, this way shadow doesn't get drawn over
	// Using the same row-by-row yielding structure as the original implementation
	for (let y = bitmapHeight - 1; y >= 0; y--) {
		const yOffset = y * bitmapWidth * 4;
		const yPosBricks = Math.round(y * brickSizePixels);

		for (let x = bitmapWidth - 1; x >= 0; x--) {
			const pixelOffset = yOffset + (x << 2);

			// Inline readUInt32BE for better performance
			const buildPixel =
				data[pixelOffset] * 0x1000000 +
				((data[pixelOffset + 1] << 16) |
					(data[pixelOffset + 2] << 8) |
					data[pixelOffset + 3]);

			const isBaseplate = buildPixel === noBrickColourRgba;
			const pixel = isBaseplate ? basePlateColourRgba : buildPixel;

			// Numeric cache key avoids allocating a string per pixel. `pixel` is a
			// 32-bit rgba (up to 0xffffffff), so use `* 2` rather than a bit-shift
			// (which would overflow 32-bit signed) to fold in the isBasePlate flag.
			const cacheKey = pixel * 2 + (isBaseplate ? 1 : 0);

			let pixelRows = brickColourCache.get(cacheKey);
			if (pixelRows === undefined) {
				const colourString = rgbaNumberToHexString(pixel);
				pixelRows = isBaseplate
					? renderBrickUnitToPixelChannelRows(
							utilityCtx,
							colourString,
							ceilBrickSizePixels,
						)
					: renderBrickToPixelChannelRows(
							utilityCtx,
							colourString,
							ceilBrickSizePixels,
						);

				// Self-gating probe: only logs on the anomaly (an empty readback for
				// a non-black colour), so it stays silent on healthy devices/servers
				// and needs no debug flag.
				const { degenerate, nonBlackPixelCount, totalPixelCount } =
					inspectSpriteReadback(pixelRows, pixel);
				if (degenerate) {
					// eslint-disable-next-line no-console
					console.error(
						`[render] degenerate sprite readback for ${colourString} ` +
							`(baseplate=${isBaseplate}) at brick size ${ceilBrickSizePixels}px: ` +
							`${nonBlackPixelCount}/${totalPixelCount} non-black pixels. ` +
							`Likely an empty getImageData readback on this device.`,
					);
				} else {
					// Guard: never cache a degenerate (empty-readback) sprite, or the
					// bad tile gets pinned for every later render at this brick size.
					// Leaving it uncached lets the next render re-rasterise and self-heal.
					// The degenerate sprite is still drawn this frame.
					brickColourCache.set(cacheKey, pixelRows);
				}
			}

			const pixelXI = (x * brickSizePixels) | 0;

			// Copy all pixel rows at once for better performance
			for (let sourceRow = 0; sourceRow < pixelRows.length; sourceRow++) {
				const pixelRow = pixelRows[sourceRow];
				const destRowOffset =
					((yPosBricks + sourceRow) * oversizedImageDataWidth + pixelXI) * 4;

				// Using TypedArray.set for faster copying
				newData.set(pixelRow, destRowOffset);
			}
		}
		// Yield after each row, exactly like the original implementation
		yield;
	}

	// Create the final image data in one operation
	ctx.putImageData(
		createImageData(
			newData,
			oversizedImageDataWidth,
			oversizedImageDataHeight,
		) as ImageData,
		offset.x,
		offset.y,
	);

	return { ceilBrickSizePixels, cache: brickColourCache };
}

export { renderPictureAsBricks };
